<template>
  <div class="user-records-page">
    <!-- Toast 通知 -->
    <transition name="toast">
      <div v-if="toast.show" class="toast-item" :class="'toast--' + toast.type" role="status" aria-live="polite">
        <svg class="toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
          <path v-if="toast.type === 'success'" d="M20 6 9 17l-5-5" />
          <template v-else-if="toast.type === 'error'">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </template>
          <template v-else-if="toast.type === 'warning'">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </template>
          <template v-else>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </template>
        </svg>
        <span class="toast-text">{{ toast.message }}</span>
      </div>
    </transition>

    <!-- 删除确认对话框 -->
    <transition name="modal">
      <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="cancelDelete">
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="delete-title">
          <div class="modal-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
          </div>
          <h3 id="delete-title" class="modal-title">确认删除</h3>
          <p class="modal-message">确定要删除该用户操作记录吗？<span class="modal-tip">此操作不可恢复。</span></p>
          <div class="modal-actions">
            <button ref="cancelBtn" type="button" class="btn btn--ghost" @click="cancelDelete">取消</button>
            <button type="button" class="btn btn--danger" @click="handleDeleteConfirm">删除</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 页面标题 -->
    <header class="page-header">
      <div class="page-header__main">
        <h1 class="page-title">用户操作记录</h1>
        <p class="page-desc">查看、下载与删除各业务模块产生的处理记录</p>
      </div>
      <span class="page-badge">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
        记录查询
      </span>
    </header>

    <!-- 概览统计 -->
    <section class="stats-grid" aria-label="概览">
      <div class="stat-card">
        <span class="stat-icon stat-icon--blue"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" /></svg></span>
        <div class="stat-body">
          <span class="stat-label">总记录</span>
          <span class="stat-value">{{ totalRecords }}</span>
        </div>
      </div>
      <div class="stat-card">
        <span class="stat-icon stat-icon--green"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></svg></span>
        <div class="stat-body">
          <span class="stat-label">处理完成</span>
          <span class="stat-value">{{ completedCount }}</span>
        </div>
      </div>
      <div class="stat-card">
        <span class="stat-icon stat-icon--amber"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg></span>
        <div class="stat-body">
          <span class="stat-label">处理中</span>
          <span class="stat-value">{{ processingCount }}</span>
        </div>
      </div>
      <div class="stat-card">
        <span class="stat-icon stat-icon--red"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="15" x2="9" y1="9" y2="15" /><line x1="9" x2="15" y1="9" y2="15" /></svg></span>
        <div class="stat-body">
          <span class="stat-label">处理失败</span>
          <span class="stat-value">{{ failedCount }}</span>
        </div>
      </div>
    </section>

    <!-- 业务状态查询 -->
    <section class="card status-card-wrap" aria-labelledby="task-status-title">
      <header class="card-header">
        <div class="card-title">
          <span class="card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 3v18h18" /><path d="m7 14 4-4 3 3 5-6" /></svg></span>
          <h2 id="task-status-title">业务状态查询</h2>
        </div>
        <div class="status-toolbar">
          <span v-if="tasksUpdatedAt" class="status-updated">更新于 {{ tasksUpdatedAt }}</span>
          <button type="button" class="btn btn--ghost" :disabled="tasksLoading" @click="fetchPendingTasks">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>
            {{ tasksLoading ? '查询中…' : '刷新' }}
          </button>
        </div>
      </header>

      <p class="card-hint">各业务当前「排队中 + 处理中」的任务数量</p>

      <p v-if="tasksError" class="status-error" role="alert">{{ tasksError }}</p>

      <div v-else class="status-grid" aria-live="polite">
        <div
          v-for="item in pendingTaskList"
          :key="item.type"
          class="biz-card"
          :class="{ 'biz-card--busy': item.count > 0 }"
        >
          <span class="biz-card__label">{{ item.label }}</span>
          <span class="biz-card__value">{{ item.count }}</span>
          <span class="biz-card__unit">个未处理</span>
        </div>
        <p v-if="pendingTaskList.length === 0" class="status-empty">
          {{ tasksLoading ? '加载中…' : '暂无业务数据' }}
        </p>
      </div>

      <p v-if="pendingTaskList.length > 0" class="status-total">
        合计 <strong>{{ pendingTaskTotal }}</strong> 个未处理任务
      </p>
    </section>

    <!-- 记录列表 -->
    <section class="card">
      <header class="card-header">
        <div class="card-title">
          <span class="card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" /></svg></span>
          <h2>操作记录列表</h2>
        </div>
        <p class="card-hint">按类型与日期筛选</p>
      </header>

      <div class="filter-bar">
        <div class="field">
          <label class="field__label" for="record-type">筛选类型</label>
          <select id="record-type" v-model="selectedRecordType" class="input">
            <option value="all">全部类型</option>
            <option value="oneclickVideoTranslation">一键视频翻译</option>
            <option value="videoDownload">视频下载</option>
            <option value="audioSubtitle">生成字幕</option>
            <option value="audioVoiceSeparation">人声分离</option>
            <option value="audioVoiceCloneTranslation">音频原语音克隆翻译</option>
            <option value="stepwiseVideoSrt">精细翻译·生成字幕</option>
            <option value="stepwiseVideoTranslation">精细翻译·克隆合成</option>
          </select>
        </div>
        <div class="field">
          <label class="field__label" for="record-date">日期</label>
          <input id="record-date" v-model="selectedDate" type="date" class="input">
        </div>
        <div class="field field--button">
          <button type="button" class="btn btn--primary" :disabled="loading" @click="queryRecords">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
            {{ loading ? '查询中…' : '查询' }}
          </button>
        </div>
      </div>

      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th class="th-filename">文件名</th>
              <th>上传时间</th>
              <th>处理状态</th>
              <th>下载</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="userRecords.length === 0">
              <td colspan="5" class="empty-tip">
                <span class="empty-tip__icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /></svg></span>
                {{ loading ? '加载中…' : '暂无操作记录' }}
              </td>
            </tr>
            <tr v-for="record in userRecords" :key="record.task_id || record.input_filename">
              <td class="td-filename" :title="record.input_filename">{{ record.input_filename || '-' }}</td>
              <td class="td-nowrap">{{ formatUploadTime(record.upload_time) }}</td>
              <td><span :class="['badge', 'badge--' + record.task_status]">{{ formatTaskStatus(record.task_status) }}</span></td>
              <td>
                <div class="download-actions">
                  <a
                    href="#"
                    class="download-link"
                    :class="{ disabled: record.task_status !== 'completed' || !record.result_file_url }"
                    @click.prevent="downloadFile(record)"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
                    {{ isOneClickRecord(record) ? '下载视频' : '下载' }}
                  </a>
                  <a
                    v-if="isOneClickRecord(record) && record.subtitle_file_url"
                    href="#"
                    class="download-link"
                    :class="{ disabled: record.task_status !== 'completed' }"
                    @click.prevent="downloadSubtitle(record)"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
                    下载字幕
                  </a>
                </div>
              </td>
              <td>
                <button type="button" class="btn btn--danger btn--sm" @click="confirmDeleteRecord(record)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" /></svg>
                  删除
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import { getUserRecords, deleteUserRecord, getPendingTasks } from '@/api/upfile'

// 音频类业务表：产出音频文件，下载时按产出格式命名
const AUDIO_RECORD_TYPES = [
  'audioVoiceSeparation',
  'audioVoiceCloneTranslation'
]

// 字幕类业务表：产出字幕文件，下载时按字幕格式命名
const SUBTITLE_RECORD_TYPES = [
  'audioSubtitle',
  'stepwiseVideoSrt'
]

// 视频类业务表：产出视频文件
const VIDEO_RECORD_TYPES = [
  'oneclickVideoTranslation',
  'stepwiseVideoTranslation'
]

// 业务类型的中文名称（业务状态查询面板使用）
const BUSINESS_LABELS = {
  oneclickVideoTranslation: '一键视频翻译',
  stepwiseVideoSrt: '精细翻译·生成字幕',
  stepwiseVideoTranslation: '精细翻译·克隆合成',
  videoDownload: '视频下载',
  audioSubtitle: '生成字幕',
  audioVoiceSeparation: '人声分离',
  audioVoiceCloneTranslation: '音频原语音克隆翻译',
  imgConvert: '图片转换',
  imgMatting: '图片抠图',
  imgUpscaleSR: '图片超分',
  videoConvert: '视频转换',
  videoSubtitle: '视频字幕',
  videoUpscaleSR: '视频超分',
  videoMatting: '视频抠图'
}

export default {
  name: 'BackendUserRecords',

  data () {
    return {
      userRecords: [],
      loading: false,
      selectedRecordType: 'all',
      selectedDate: '',
      pendingDeleteRecord: null,
      showDeleteConfirm: false,
      toast: {
        show: false,
        message: '',
        type: 'success',
        timer: null
      },
      // 仅保留从三个业务页面抽取出来的记录类型
      serviceTypes: [
        'oneclickVideoTranslation',
        'stepwiseVideoSrt',
        'stepwiseVideoTranslation',
        'videoDownload',
        'audioSubtitle',
        'audioVoiceSeparation',
        'audioVoiceCloneTranslation'
      ],
      // 业务状态查询
      pendingTasks: {},
      tasksLoading: false,
      tasksError: '',
      tasksUpdatedAt: ''
    }
  },

  computed: {
    pendingTaskList () {
      return Object.keys(this.pendingTasks).map(type => ({
        type,
        label: BUSINESS_LABELS[type] || type,
        count: Number(this.pendingTasks[type]) || 0
      }))
    },
    pendingTaskTotal () {
      return this.pendingTaskList.reduce((sum, item) => sum + item.count, 0)
    },
    totalRecords () {
      return this.userRecords.length
    },
    completedCount () {
      return this.userRecords.filter(r => r.task_status === 'completed').length
    },
    processingCount () {
      return this.userRecords.filter(r => r.task_status === 'processing').length
    },
    failedCount () {
      return this.userRecords.filter(r => r.task_status === 'failed').length
    }
  },

  created () {
    this.queryRecords()
    this.fetchPendingTasks()
  },

  mounted () {
    window.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy () {
    window.removeEventListener('keydown', this.handleKeydown)
    if (this.toast.timer) {
      clearTimeout(this.toast.timer)
    }
  },

  methods: {
    async fetchPendingTasks () {
      if (this.tasksLoading) return
      this.tasksLoading = true
      this.tasksError = ''
      try {
        const response = await getPendingTasks()
        if (response && response.status === 200 && response.result) {
          this.pendingTasks = response.result
          this.tasksUpdatedAt = this.formatNow()
        } else {
          this.tasksError = (response && response.message) || '查询业务状态失败'
        }
      } catch (error) {
        console.error('查询业务状态失败:', error)
        this.tasksError = '查询业务状态失败，请稍后重试'
      } finally {
        this.tasksLoading = false
      }
    },

    formatNow () {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    },

    queryRecords () {
      if (this.loading) return
      this.fetchUserRecords()
    },

    async fetchUserRecords () {
      this.loading = true
      try {
        // 保持原三个页面使用的 getUserRecords 接口调用与 table_names 参数
        const tableNames = this.selectedRecordType === 'all'
          ? this.serviceTypes
          : [this.selectedRecordType]

        const data = {
          table_names: tableNames
        }

        const response = await getUserRecords(data)

        const rows = (response.result || []).slice()
        const filtered = this.selectedDate
          ? rows.filter(record => this.formatUploadTime(record.upload_time).startsWith(this.selectedDate))
          : rows

        this.userRecords = filtered.sort((a, b) => {
          return new Date(b.upload_time) - new Date(a.upload_time)
        })
      } catch (error) {
        console.error('获取用户操作记录失败:', error)
        this.showToast('获取用户记录失败', 'error')
      } finally {
        this.loading = false
      }
    },

    confirmDeleteRecord (record) {
      this.pendingDeleteRecord = record
      this.showDeleteConfirm = true
      this.$nextTick(() => {
        if (this.$refs.cancelBtn) this.$refs.cancelBtn.focus()
      })
    },

    cancelDelete () {
      this.showDeleteConfirm = false
      this.pendingDeleteRecord = null
    },

    async handleDeleteConfirm () {
      this.showDeleteConfirm = false
      const record = this.pendingDeleteRecord
      this.pendingDeleteRecord = null
      if (!record) return

      try {
        // 保持原三个页面使用的 deleteUserRecord 接口调用及请求参数字段
        const payload = {
          operation_type: record.operation_type || this.selectedRecordType,
          phoneNum: record.user_phone || record.phoneNum,
          taskId: record.task_id
        }
        const response = await deleteUserRecord(payload)

        if (response.status === 200) {
          const backendMsg = response.message || '删除成功'
          const shouldRemove = !backendMsg.includes('记录不存在') &&
                              !backendMsg.includes('无需手动删除')

          if (shouldRemove) {
            this.userRecords = this.userRecords.filter(r => r.task_id !== record.task_id)
            this.showToast(backendMsg, 'success')
          } else {
            await this.fetchUserRecords()
            this.showToast(backendMsg, 'info')
          }
        } else {
          this.showToast(`删除失败：${response.message || '未知错误'}`, 'error')
        }
      } catch (error) {
        console.error('删除记录失败:', error)
        this.showToast('删除记录失败', 'error')
      }
    },

    downloadFile (record) {
      if (record.task_status === 'completed' && record.result_file_url) {
        const link = document.createElement('a')
        link.href = record.result_file_url
        link.download = this.buildDownloadName(record)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        this.showToast('文件处理尚未完成或处理失败，无法下载', 'warning')
      }
    },

    // 从结果文件地址中解析产出格式（扩展名）
    getResultExtension (record) {
      const url = record && record.result_file_url
      if (!url) return ''
      const cleanUrl = String(url).split('?')[0].split('#')[0]
      const match = cleanUrl.match(/\.([A-Za-z0-9]{1,8})$/)
      return match ? match[1].toLowerCase() : ''
    },

    isAudioRecord (record) {
      const type = (record && record.operation_type) || this.selectedRecordType
      return AUDIO_RECORD_TYPES.indexOf(type) !== -1
    },

    isSubtitleRecord (record) {
      const type = (record && record.operation_type) || this.selectedRecordType
      return SUBTITLE_RECORD_TYPES.indexOf(type) !== -1
    },

    isVideoRecord (record) {
      const type = (record && record.operation_type) || this.selectedRecordType
      return VIDEO_RECORD_TYPES.indexOf(type) !== -1
    },

    // 音频类记录的产出格式：优先取数据表里的格式字段，其次从结果文件地址推断
    getAudioOutputFormat (record) {
      if (!record) return ''
      const fromField = record.subtitle_format || record.output_format
      if (fromField) return String(fromField).toLowerCase()
      return this.getResultExtension(record)
    },

    buildDownloadName (record) {
      const baseName = (record.input_filename || 'download').replace(/\.[^/.]+$/, '')

      // 视频类业务：产出视频文件
      if (this.isVideoRecord(record)) {
        return `${baseName}_translated.mp4`
      }

      // 字幕类业务：按字幕格式命名（audioSubtitle 的 subtitle_format / 精细翻译生成的字幕）
      if (this.isSubtitleRecord(record)) {
        const ext = record.subtitle_format ? String(record.subtitle_format).toLowerCase() : (this.getResultExtension(record) || 'srt')
        return `${baseName}.${ext}`
      }

      // 音频类业务：按产出格式命名（人声分离 output_format / 语音克隆翻译 wav）
      if (this.isAudioRecord(record)) {
        const ext = this.getAudioOutputFormat(record)
        return ext ? `${baseName}.${ext}` : (record.input_filename || 'download')
      }

      return record.input_filename || 'download'
    },

    downloadSubtitle (record) {
      if (record.task_status !== 'completed' || !record.subtitle_file_url) {
        this.showToast('字幕尚未生成，无法下载', 'warning')
        return
      }

      const link = document.createElement('a')
      link.href = record.subtitle_file_url
      const baseName = (record.input_filename || 'subtitle').replace(/\.[^/.]+$/, '')
      link.download = `${baseName}.srt`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    isOneClickRecord (record) {
      return record && record.operation_type === 'oneclickVideoTranslation'
    },

    formatUploadTime (time) {
      if (!time) return '-'
      const value = String(time)
      const pad = n => String(n).padStart(2, '0')

      // 后端返回的是不带时区的“服务器本地时间”字符串（YYYY-MM-DD HH:MM:SS 或 YYYY-MM-DDTHH:MM:SS）时，
      // 直接按字面量展示，避免 new Date 再按浏览器时区换算。
      const plain = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/)
      if (plain) {
        return `${plain[1]}-${plain[2]}-${plain[3]} ${plain[4]}:${plain[5]}:${plain[6]}`
      }

      // 后端把 DATETIME 序列化成 GMT/UTC（Flask 会这样处理），用 UTC 分量还原服务器保存的原始时间，
      // 不能再用本地 getHours()。
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
      }

      return value
    },

    formatTaskStatus (status) {
      const statusMap = {
        pending: '等待处理',
        processing: '正在处理',
        completed: '处理完成',
        failed: '处理失败'
      }
      return statusMap[status] || status || '-'
    },

    showToast (message, type = 'success') {
      if (this.toast.timer) {
        clearTimeout(this.toast.timer)
      }
      this.toast.message = message
      this.toast.type = type
      this.toast.show = true
      this.toast.timer = setTimeout(() => {
        this.toast.show = false
      }, 2500)
    },

    handleKeydown (e) {
      if (e.key === 'Escape' && this.showDeleteConfirm) {
        this.cancelDelete()
      }
    }
  }
}
</script>

<style lang="less" scoped>
// ==================== 设计令牌 ====================
@primary: #3555f4;
@primary-hover: #2a44d0;
@primary-soft: #eef1ff;
@success: #16a34a;
@success-soft: #ecfdf5;
@warning: #d97706;
@warning-soft: #fffbeb;
@danger: #dc2626;
@danger-soft: #fef2f2;
@info: #409eff;
@info-soft: #eff6ff;

@bg: #f5f7fb;
@surface: #ffffff;
@text: #1f2937;
@text-secondary: #64748b;
@text-muted: #94a3b8;
@border: #e5e7eb;
@border-strong: #d1d5db;

@radius: 12px;
@radius-sm: 8px;
@shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
@shadow-lg: 0 10px 30px rgba(15, 23, 42, 0.12);

@font: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;

.user-records-page {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 8px 4px 56px;
  font-family: @font;
  color: @text;
  font-size: 14px;
  line-height: 1.55;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
}

svg {
  width: 1em;
  height: 1em;
  flex-shrink: 0;
}

// ==================== 页面标题 ====================
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 4px 2px 20px;

  .page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.2px;
    color: @text;
  }

  .page-desc {
    margin: 4px 0 0;
    font-size: 13px;
    color: @text-secondary;
  }

  .page-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 999px;
    background: @primary-soft;
    color: @primary;
    font-size: 12px;
    font-weight: 600;

    svg {
      width: 14px;
      height: 14px;
    }
  }
}

// ==================== 统计概览 ====================
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 18px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: @surface;
  border: 1px solid @border;
  border-radius: @radius;
  padding: 18px;
  box-shadow: @shadow;
}

.stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  font-size: 20px;

  &--blue  { background: @primary-soft; color: @primary; }
  &--green { background: @success-soft; color: @success; }
  &--amber { background: @warning-soft; color: @warning; }
  &--red   { background: @danger-soft; color: @danger; }
}

.stat-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.stat-label {
  font-size: 12px;
  color: @text-secondary;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: @text;
}

// ==================== 卡片 ====================
.card {
  background: @surface;
  border: 1px solid @border;
  border-radius: @radius;
  padding: 22px 24px 24px;
  box-shadow: @shadow;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;

  h2 {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: @text;
  }

  .card-hint {
    margin: 0;
    font-size: 12px;
    color: @text-muted;
  }
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: @primary-soft;
  color: @primary;
  font-size: 17px;
}

// ==================== 业务状态查询 ====================
.status-card-wrap {
  margin-bottom: 18px;
}

.status-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.status-updated {
  font-size: 12px;
  color: @text-muted;
  font-variant-numeric: tabular-nums;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.biz-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px 16px;
  background: #f9fafb;
  border: 1px solid @border;
  border-radius: @radius-sm;

  &__label {
    font-size: 12px;
    color: @text-secondary;
    line-height: 1.4;
  }

  &__value {
    font-size: 24px;
    font-weight: 700;
    color: @text-muted;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }

  &__unit {
    font-size: 12px;
    color: @text-muted;
  }

  &--busy {
    background: @warning-soft;
    border-color: rgba(217, 119, 6, 0.35);

    .biz-card__value {
      color: @warning;
    }
  }
}

.status-total {
  margin: 14px 0 0;
  font-size: 13px;
  color: @text-secondary;

  strong {
    color: @text;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
  }
}

.status-error {
  margin: 14px 0 0;
  padding: 10px 14px;
  border-radius: @radius-sm;
  background: @danger-soft;
  color: @danger;
  font-size: 13px;
}

.status-empty {
  margin: 0;
  padding: 16px;
  text-align: center;
  color: @text-muted;
  font-size: 13px;
}

// ==================== 筛选 ====================
.filter-bar {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  &--button {
    justify-content: flex-end;
    align-self: end;
  }

  .field__label {
    font-size: 13px;
    font-weight: 500;
    color: @text-secondary;
  }
}

.input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  background: @surface;
  color: @text;
  border: 1px solid @border-strong;
  border-radius: @radius-sm;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder {
    color: @text-muted;
  }

  &:hover {
    border-color: #a5b4fc;
  }

  &:focus {
    outline: none;
    border-color: @primary;
    box-shadow: 0 0 0 3px rgba(53, 85, 244, 0.16);
  }
}

select.input {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 32px;
  cursor: pointer;
}

// ==================== 按钮 ====================
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: @radius-sm;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s, transform 0.05s, box-shadow 0.15s;

  svg {
    width: 15px;
    height: 15px;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid @primary;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &--primary {
    background: @primary;
    color: #fff;

    &:hover:not(:disabled) {
      background: @primary-hover;
      box-shadow: 0 4px 12px rgba(53, 85, 244, 0.24);
    }
  }

  &--danger {
    background: @danger;
    color: #fff;

    &:hover:not(:disabled) {
      background: #b91c1c;
      box-shadow: 0 4px 12px rgba(220, 38, 38, 0.24);
    }
  }

  &--ghost {
    background: @surface;
    color: @text-secondary;
    border-color: @border-strong;

    &:hover:not(:disabled) {
      color: @text;
      border-color: @text-muted;
      background: #f9fafb;
    }
  }

  &--sm {
    min-height: 34px;
    padding: 0 12px;
    font-size: 13px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
}

// ==================== 表格 ====================
.table-wrap {
  overflow: auto;
  max-height: 520px;
  border: 1px solid @border;
  border-radius: @radius-sm;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 680px;

  th,
  td {
    padding: 11px 14px;
    text-align: left;
    border-bottom: 1px solid @border;
    vertical-align: middle;
  }

  .th-filename {
    width: 38%;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
    color: @text-secondary;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.2px;
    white-space: nowrap;
  }

  tbody tr {
    transition: background-color 0.12s;

    &:hover {
      background: #f8fafc;
    }

    &:last-child td {
      border-bottom: none;
    }
  }
}

.td-filename {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  color: @text;
}

.td-nowrap {
  white-space: nowrap;
  color: @text-secondary;
  font-variant-numeric: tabular-nums;
}

.empty-tip {
  text-align: center;
  padding: 40px 16px !important;
  color: @text-muted;
  font-size: 13px;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    margin-bottom: 10px;
    border-radius: 50%;
    background: #f1f5f9;
    color: @text-muted;
    font-size: 20px;
  }
}

// ==================== 状态徽章 ====================
.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.6;
  white-space: nowrap;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  &--completed {
    background: @success-soft;
    color: @success;
  }

  &--processing {
    background: @warning-soft;
    color: @warning;
  }

  &--failed {
    background: @danger-soft;
    color: @danger;
  }

  &--pending {
    background: #f1f5f9;
    color: @text-secondary;
  }
}

// ==================== 下载链接 ====================
.download-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.download-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: @primary;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  padding: 5px 9px;
  border-radius: 6px;
  transition: background-color 0.15s, color 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover:not(.disabled) {
    background: @primary-soft;
  }

  &:focus-visible {
    outline: 2px solid @primary;
    outline-offset: 1px;
  }

  &.disabled {
    color: @text-muted;
    pointer-events: none;
    cursor: not-allowed;
  }
}

// ==================== Toast ====================
.toast-item {
  position: fixed;
  top: 76px;
  right: 20px;
  z-index: 9997;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 220px;
  max-width: 340px;
  padding: 11px 14px;
  border-radius: @radius-sm;
  color: #fff;
  box-shadow: @shadow-lg;

  .toast-icon {
    width: 17px;
    height: 17px;
    flex-shrink: 0;
  }

  .toast-text {
    flex: 1;
    font-size: 13px;
    line-height: 1.5;
  }

  &--success { background: @success; }
  &--error   { background: @danger; }
  &--warning { background: @warning; }
  &--info    { background: @info; }
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateX(16px);
}

// ==================== 对话框 ====================
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9996;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.45);
}

.modal {
  width: 100%;
  max-width: 400px;
  background: @surface;
  border-radius: 14px;
  padding: 24px;
  box-shadow: @shadow-lg;

  .modal-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    margin-bottom: 14px;
    background: @danger-soft;
    color: @danger;
    font-size: 22px;
  }

  .modal-title {
    margin: 0 0 8px;
    font-size: 17px;
    font-weight: 600;
    color: @text;
  }

  .modal-message {
    margin: 0 0 20px;
    font-size: 14px;
    color: @text-secondary;
    line-height: 1.65;

    .modal-tip {
      display: block;
      margin-top: 2px;
      color: @danger;
      font-weight: 500;
    }
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
}
.modal-enter,
.modal-leave-to {
  opacity: 0;
}
.modal-enter .modal,
.modal-leave-to .modal {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

// ==================== 滚动条 ====================
.table-wrap::-webkit-scrollbar {
  width: 9px;
  height: 9px;
}
.table-wrap::-webkit-scrollbar-track {
  background: transparent;
}
.table-wrap::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 5px;

  &:hover {
    background: #94a3b8;
  }
}

// ==================== 响应式 ====================
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .user-records-page {
    padding: 0 0 40px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }

  .card {
    padding: 18px 16px 20px;
  }

  .filter-bar {
    align-items: stretch;

    .field--button {
      align-self: stretch;
    }

    .btn {
      width: 100%;
    }
  }

  .toast-item {
    right: 12px;
    left: 12px;
    max-width: none;
  }
}

// ==================== 减少动态效果 ====================
@media (prefers-reduced-motion: reduce) {
  .btn,
  .input,
  .toast-item,
  .modal-overlay,
  .download-link,
  .table tbody tr {
    transition: none !important;
  }
}
</style>
